import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@material-ui/core/styles';
import { ReloadableResultStatus } from 'api/resultStatus';
import { ContextResponse, defaultContext } from 'contexts/appContext';
import React, { useEffect } from 'react';

import { cookieConsentReady, getCookieConsent } from 'components/CookieConsent';
import { ErrorPage } from 'components/ErrorPage';
import { LoadingBoundary } from 'components/LoadingBoundary';
import { StylesProvider } from 'components/StylesProvider';

import { LanguageProvider } from '../../i18n/LanguageProvider';
import { buildApplicantBrandedTheme } from '../../theme';

/**
 * React Hook which adds account metadata to the `ftn` property of Datadog's
 * global context for both Logs and RUM
 */
export const useSetDatadogAccountGlobalContext = (
  result: ReloadableResultStatus<
    // eslint-disable-next-line camelcase
    { account: { external_id: string; name: string; slug: string } },
    unknown // Error state isn't releveant for the hook
  >,
) => {
  React.useEffect(() => {
    if (result.status === 'ready') {
      // Wait for Transcend or the context setters will get called before
      // Datadog SDK initalization occurs
      void cookieConsentReady().then(() => {
        [datadogLogs, datadogRum].forEach(datadogSdk => {
          const currentContext = datadogSdk.getGlobalContext();
          datadogSdk.setGlobalContextProperty('ftn', {
            ...(typeof currentContext.ftn === 'object'
              ? currentContext.ftn
              : null),
            account: {
              external_id: result.data.account.external_id,
              name: result.data.account.name,
              slug: result.data.account.slug,
            },
          });
        });
      });
    }
  }, [result]);
};

interface Funnel {
  locale: string;
  // eslint-disable-next-line camelcase
  display_locale?: string;
}
interface Config {
  // eslint-disable-next-line camelcase
  cookie_consent_enabled: boolean;
}
interface Setup {
  funnel: Funnel;
  config: Config;
  account: ContextResponse['account'];
  brand: ContextResponse['brand'];
}
export type SetupProps = ReloadableResultStatus<Setup, unknown> & {
  timeZone?: string;
  loading?: boolean;
};

/*
 * This file handles common setup for i18n and theming.
 */
export const AppSetup: React.FC<SetupProps> = ({
  timeZone,
  loading,
  ...props
}) => {
  useEffect(() => {
    if (props.status === 'ready') {
      getCookieConsent(props.data.config.cookie_consent_enabled)
        .then()
        // eslint-disable-next-line no-console
        .catch(e => console.error(e));
    }
  }, [props]);

  useSetDatadogAccountGlobalContext(props);

  return (
    <LoadingBoundary loading={loading ?? props.status === 'loading'}>
      <LanguageProvider
        locale={
          props.status === 'ready'
            ? props.data.funnel.display_locale ?? props.data.funnel.locale
            : window.navigator.language
        }
        timeZone={timeZone}
      >
        <>
          {props.status === 'error' && (
            <ThemeProvider
              theme={buildApplicantBrandedTheme(defaultContext.brand.colors)}
            >
              <StylesProvider>
                <ErrorPage />
              </StylesProvider>
            </ThemeProvider>
          )}
          {props.status === 'ready' && (
            <ThemeProvider
              theme={buildApplicantBrandedTheme(props.data.brand.colors)}
            >
              <StylesProvider>{props.children}</StylesProvider>
            </ThemeProvider>
          )}
        </>
      </LanguageProvider>
    </LoadingBoundary>
  );
};
